import { useMutation, useQueryClient } from "@tanstack/react-query";
import { vaultKeys } from "../queryKeys";
import {
  bdcApi,
  type BdcResendMethod,
  type BdcStateResponse,
} from "../../../api/bdc.api";
import { useAppSelector } from "../../../redux/hooks";
import { getVaultActorAuthScope } from "../../../utils/vault/vaultActorSession";

interface BdcResendPinParams {
  reqId: string;
  method: BdcResendMethod;
}

/**
 * POST bdc/resend-pin — reenvía el NIP por Email o SMS.
 *
 * Refresca la cache de `bdc/status` con el estado devuelto (incluye el nuevo
 * `resend_pin`). NO swallowea el error: el componente mapea el code con
 * `mapBdcAxiosError` (1014 bloquea, etc.).
 */
export function useBdcResendPin(folderId?: string) {
  const queryClient = useQueryClient();
  const jwtToken = useAppSelector((state) => state.vault.jwtToken);
  const authScope = getVaultActorAuthScope(jwtToken);

  return useMutation<BdcStateResponse, unknown, BdcResendPinParams>({
    mutationFn: ({ reqId, method }) => bdcApi.resendPin(reqId, { method }),
    onMutate: async ({ reqId }) => {
      await queryClient.cancelQueries({
        queryKey: vaultKeys.bdcStatus(reqId, authScope),
      });
    },
    onSuccess: async (data, { reqId }) => {
      const queryKey = vaultKeys.bdcStatus(reqId, authScope);
      // Reconnection can start another GET while the POST is in flight.
      await queryClient.cancelQueries({ queryKey });
      queryClient.setQueryData(
        queryKey,
        (prev: BdcStateResponse | undefined) => ({
          ...prev,
          ...data,
          // A terminal result can replace the preceding GET's local verdict.
          requirement_status: data.requirement_status,
        }),
      );
      if (
        ["completed", "in_review", "invalid"].includes(data.status) &&
        folderId
      ) {
        void queryClient.invalidateQueries({
          queryKey: vaultKeys.folder(folderId),
        });
      }
    },
  });
}
